import React, { useState, useEffect } from "react";
// import { useSelector } from "react-redux";
// import type { RootState } from "../../../store";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  DocumentTextIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  BookmarkIcon as PinIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
// import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import Button from "../../../components/ui/Button";
import Card, { CardContent } from "../../../components/ui/Card";
import Modal, { ModalContent, ModalFooter } from "../../../components/ui/Modal";
import FormInput from "../../../components/ui/FormInput";
import Textarea from "../../../components/ui/Textarea";
import FileUpload from "../../../components/upload/FileUpload";
import RichTextEditor from "../../../components/editor/RichTextEditor";
// import VideoPlayer from "../../../components/media/VideoPlayer";
// import EmojiReactions from "../../../components/reactions/EmojiReactions";
import PostPreview from "../../../components/content/PostPreview";
import { postsService } from "../../../services/postsService";
import { creatorService } from "../../../services/creatorService";

interface Post {
  id: string;
  title?: string;
  content: string;
  contentType: "TEXT" | "IMAGE" | "VIDEO" | "AUDIO" | "POLL" | "LINK";
  mediaUrls: string[];
  isPublic: boolean;
  isScheduled: boolean;
  publishedAt?: string;
  scheduledFor?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    likes: number;
    comments: number;
  };
  likes?: any[];
  comments?: any[];
}

interface PostFormData {
  title?: string;
  content: string;
  contentType: "TEXT" | "IMAGE" | "VIDEO" | "AUDIO" | "POLL" | "LINK";
  mediaUrls: string[];
  isPublic: boolean;
  isScheduled: boolean;
  scheduledFor?: string;
  tiers: string[];
  pollOptions?: string[]; // For POLL content type
}

const PostsPage: React.FC = () => {
  // const { user } = useSelector((state: RootState) => state.auth);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PostFormData>({
    title: "",
    content: "",
    contentType: "TEXT",
    mediaUrls: [],
    isPublic: false,
    isScheduled: false,
    scheduledFor: "",
    tiers: [],
    pollOptions: [],
  });
  const [activeFilter, setActiveFilter] = useState<
    "all" | "draft" | "published" | "scheduled"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [tiersOptions, setTiersOptions] = useState<{ id: string; name: string }[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadPosts();
    loadTiers();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await postsService.list({ limit: 20, offset: 0 });
      setPosts(res.posts || []);
    } catch (error) {
      console.error("Failed to load posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTiers = async () => {
    try {
      const tiers = await creatorService.getMyTiers();
      setTiersOptions(tiers.map((t: any) => ({ id: t.id, name: t.name })));
    } catch (e) {
      // ignore if not a creator yet
    }
  };

  const handleOpenModal = (post?: Post) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || "",
        content: post.content,
        contentType: post.contentType,
        mediaUrls: post.mediaUrls,
        isPublic: post.isPublic,
        isScheduled: post.isScheduled,
        scheduledFor: post.scheduledFor,
        tiers: [],
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: "",
        content: "",
        contentType: "TEXT",
        mediaUrls: [],
        isPublic: false,
        isScheduled: false,
        tiers: [],
        pollOptions: [],
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPost(null);
    setFormData({
      title: "",
      content: "",
      contentType: "TEXT",
      mediaUrls: [],
      isPublic: false,
      isScheduled: false,
      tiers: [],
      pollOptions: [],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate poll options
      if (formData.contentType === 'POLL') {
        const validOptions = (formData.pollOptions || []).filter(opt => opt.trim());
        if (validOptions.length < 2) {
          alert('Polls require at least 2 options');
          setIsSubmitting(false);
          return;
        }
      }

      const payload: any = {
        title: formData.title || undefined,
        content: formData.content,
        contentType: formData.contentType,
        isPublic: formData.isPublic,
        isScheduled: formData.isScheduled,
        publishedAt: formData.isScheduled && formData.scheduledFor ? formData.scheduledFor : formData.isPublic ? new Date().toISOString() : null,
        tierIds: formData.tiers.length > 0 ? formData.tiers : undefined,
        images: uploadedFiles?.length ? uploadedFiles.map((f: any) => f.file || f) : undefined,
      };

      // Add poll options if it's a poll
      if (formData.contentType === 'POLL' && formData.pollOptions) {
        payload.pollOptions = formData.pollOptions.filter(opt => opt.trim());
      }

      if (editingPost) {
  await postsService.update(editingPost.id, payload);
        await loadPosts();
      } else {
  await postsService.create(payload);
        await loadPosts();
      }

      handleCloseModal();
    } catch (error) {
      console.error("Failed to save post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this post? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await postsService.remove(postId);
      setPosts((prev) => prev.filter((post) => post.id !== postId));
    } catch (error) {
      console.error("Failed to delete post:", error);
    }
  };

  const handleTogglePublish = async (postId: string) => {
    try {
      const current = posts.find(p => p.id === postId);
      if (!current) return;
      await postsService.update(postId, {
        isPublic: !current.isPublic,
        publishedAt: !current.isPublic ? new Date().toISOString() : null,
      });
      await loadPosts();
    } catch (error) {
      console.error("Failed to toggle post status:", error);
    }
  };

  const handleTogglePin = async (postId: string) => {
    try {
      await postsService.togglePin(postId);
      await loadPosts();
    } catch (error) {
      console.error("Failed to toggle pin:", error);
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case "VIDEO":
        return <VideoCameraIcon className="w-5 h-5" />;
      case "AUDIO":
        return <MusicalNoteIcon className="w-5 h-5" />;
      case "IMAGE":
        return <PhotoIcon className="w-5 h-5" />;
      default:
        return <DocumentTextIcon className="w-5 h-5" />;
    }
  };

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case "VIDEO":
        return "bg-red-100 text-red-700";
      case "AUDIO":
        return "bg-purple-100 text-purple-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content.toLowerCase().includes(searchTerm.toLowerCase());

    switch (activeFilter) {
      case "draft":
        return matchesSearch && !post.isPublic && !post.isScheduled;
      case "published":
        return matchesSearch && post.isPublic && !post.isScheduled;
      case "scheduled":
        return matchesSearch && post.isScheduled;
      default:
        return matchesSearch;
    }
  });

  const draftCount = posts.filter((p) => !p.isPublic && !p.isScheduled).length;
  const publishedCount = posts.filter(
    (p) => p.isPublic && !p.isScheduled,
  ).length;
  const scheduledCount = posts.filter((p) => p.isScheduled).length;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Posts</h1>
          <p className="text-gray-600 mt-2">
            Create and manage your content for patrons
          </p>
        </div>
        <Button
          leftIcon={<PlusIcon className="w-5 h-5" />}
          onClick={() => handleOpenModal()}
        >
          Create Post
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {posts.length}
                </p>
                <p className="text-sm text-gray-600">Total Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {publishedCount}
                </p>
                <p className="text-sm text-gray-600">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <ClockIcon className="w-6 h-6" style={{ color: 'var(--patreon-orange)' }} />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{draftCount}</p>
                <p className="text-sm text-gray-600">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {scheduledCount}
                </p>
                <p className="text-sm text-gray-600">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setActiveFilter("all")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeFilter === "all"
                ? "bg-orange-100 text-orange-700"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            All ({posts.length})
          </button>
          <button
            onClick={() => setActiveFilter("published")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeFilter === "published"
                ? "bg-orange-100 text-orange-700"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => setActiveFilter("draft")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeFilter === "draft"
                ? "bg-orange-100 text-orange-700"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Drafts ({draftCount})
          </button>
          <button
            onClick={() => setActiveFilter("scheduled")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeFilter === "scheduled"
                ? "bg-orange-100 text-orange-700"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Scheduled ({scheduledCount})
          </button>
        </div>

        <FormInput
          type="search"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          containerClassName="w-full sm:w-auto"
        />
      </div>

      {/* Posts List */}
      <div className="space-y-6">
        {filteredPosts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No posts found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm
                  ? "Try adjusting your search terms."
                  : "Create your first post to get started."}
              </p>
              {!searchTerm && (
                <Button
                  leftIcon={<PlusIcon className="w-5 h-5" />}
                  onClick={() => handleOpenModal()}
                >
                  Create Your First Post
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredPosts.map((post) => (
            <Card
              key={post.id}
              className={`${!post.isPublic && !post.isScheduled ? "opacity-75" : ""}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {post.title || "Untitled Post"}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getContentTypeColor(post.contentType)}`}
                      >
                        {getContentTypeIcon(post.contentType)}
                        <span className="ml-1 capitalize">
                          {post.contentType}
                        </span>
                      </span>
                      {post.isScheduled && (
                        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                          Scheduled
                        </span>
                      )}
                      {post.isPublic ? (
                        <CheckCircleIcon
                          className="w-5 h-5 text-green-500"
                          title="Published"
                        />
                      ) : post.isScheduled ? (
                        <ClockIcon
                          className="w-5 h-5 text-purple-500"
                          title="Scheduled"
                        />
                      ) : (
                        <XCircleIcon
                          className="w-5 h-5 text-gray-400"
                          title="Draft"
                        />
                      )}
                    </div>

                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.content}
                    </p>

                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {post.publishedAt
                          ? formatDate(post.publishedAt)
                          : formatDate(post.createdAt)}
                      </span>
                      {post.scheduledFor && (
                        <span className="flex items-center text-purple-600">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          Scheduled for {formatDate(post.scheduledFor)}
                        </span>
                      )}
                      <span className="flex items-center">
                        <HeartIcon className="w-4 h-4 mr-1" />
                        {post._count?.likes || 0}
                      </span>
                      <span className="flex items-center">
                        <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" />
                        {post._count?.comments || 0}
                      </span>
                    </div>

                    {post.mediaUrls.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Attachments:
                        </p>
                        <div className="flex space-x-2">
                          {post.mediaUrls.map((url, index) => (
                            <div
                              key={index}
                              className="bg-gray-100 px-3 py-1 rounded-md text-sm text-gray-600"
                            >
                              Media {index + 1}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col space-y-2 ml-6">
                    <button
                      onClick={() => handleOpenModal(post)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>

                    {!post.isScheduled && (
                      <>
                        <button
                          onClick={() => handleTogglePin(post.id)}
                          className={`p-2 rounded-lg ${(post as any).isPinned ? 'text-orange-600 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'}`}
                          title={(post as any).isPinned ? 'Unpin post' : 'Pin post'}
                        >
                          <PinIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleTogglePublish(post.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        >
                          {post.isPublic ? (
                            <EyeSlashIcon className="w-4 h-4" />
                          ) : (
                            <EyeIcon className="w-4 h-4" />
                          )}
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Post Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={showPreview ? "Post Preview" : (editingPost ? "Edit Post" : "Create New Post")}
        size="3xl"
      >
        <form onSubmit={handleSubmit}>
          <ModalContent>
            {showPreview ? (
              /* Preview Mode */
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 flex items-center gap-2">
                    <EyeIcon className="w-5 h-5" />
                    <span>This is how your post will appear to your patrons</span>
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-6">
                  {formData.title && (
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{formData.title}</h2>
                  )}
                  <PostPreview
                    title={formData.title}
                    content={formData.content}
                    contentType={formData.contentType}
                    mediaUrls={formData.mediaUrls}
                    isPublic={formData.isPublic}
                    publishedAt={formData.isScheduled ? formData.scheduledFor : new Date().toISOString()}
                  />
                  {formData.isScheduled && formData.scheduledFor && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800 flex items-center gap-2">
                        <ClockIcon className="w-5 h-5" />
                        <span>Scheduled for: {new Date(formData.scheduledFor).toLocaleString()}</span>
                      </p>
                    </div>
                  )}
                  {!formData.isPublic && formData.tiers.length > 0 && (
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
                      <p className="text-sm text-purple-800 flex items-center gap-2">
                        <LockClosedIcon className="w-5 h-5" />
                        <span>Visible to selected tier members only</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Edit Mode */
            <div className="space-y-6">
              <FormInput
                label="Title (Optional)"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Give your post a title"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Type
                </label>
                <select
                  value={formData.contentType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contentType: e.target.value as any,
                    }))
                  }
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-offset-2"
                  style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                >
                  <option value="TEXT">Text</option>
                  <option value="IMAGE">Image</option>
                  <option value="VIDEO">Video</option>
                  <option value="AUDIO">Audio</option>
                  <option value="POLL">Poll</option>
                  <option value="LINK">Link</option>
                </select>
              </div>

              {/* Tier visibility */}
              {tiersOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Restrict to tiers (optional)</label>
                  <select
                    multiple
                    value={formData.tiers}
                    onChange={(e) => {
                      const opts = Array.from(e.target.selectedOptions).map(o => o.value);
                      setFormData(prev => ({ ...prev, tiers: opts }));
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-offset-2"
                  style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                  >
                    {tiersOptions.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--patreon-black)' }}>
                  Content
                </label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, content: value }))
                  }
                  placeholder="Write your post content here..."
                  disabled={false}
                />
              </div>

              {formData.contentType === "POLL" && (
                <div className="space-y-4">
                  <label className="block text-sm font-medium" style={{ color: 'var(--patreon-black)' }}>
                    Poll Options (minimum 2, maximum 10)
                  </label>
                  {(formData.pollOptions || []).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <FormInput
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(formData.pollOptions || [])];
                          newOptions[index] = e.target.value;
                          setFormData({ ...formData, pollOptions: newOptions });
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                      />
                      {(formData.pollOptions && formData.pollOptions.length > 2) && (
                        <button
                          type="button"
                          onClick={() => {
                            const newOptions = formData.pollOptions?.filter((_, i) => i !== index) || [];
                            setFormData({ ...formData, pollOptions: newOptions });
                          }}
                          className="p-2" style={{ color: 'var(--patreon-error)' }}
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {(!formData.pollOptions || formData.pollOptions.length < 10) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          pollOptions: [...(formData.pollOptions || []), '']
                        });
                      }}
                      leftIcon={<PlusIcon className="w-4 h-4" />}
                    >
                      Add Option
                    </Button>
                  )}
                  {(!formData.pollOptions || formData.pollOptions.length < 2) && (
                    <p className="text-sm" style={{ color: 'var(--patreon-error)' }}>
                      Polls require at least 2 options
                    </p>
                  )}
                </div>
              )}

              {(formData.contentType === "IMAGE" ||
                formData.contentType === "VIDEO" ||
                formData.contentType === "AUDIO") && (
                <div className="space-y-4">
                  <FileUpload
                    accept={
                      formData.contentType === "IMAGE"
                        ? "image/*"
                        : formData.contentType === "VIDEO"
                          ? "video/*"
                          : formData.contentType === "AUDIO"
                            ? "audio/*"
                            : "*/*"
                    }
                    multiple={true}
                    maxSize={50}
                    maxFiles={5}
                    value={uploadedFiles}
                    onChange={setUploadedFiles}
                    showPreview={true}
                    onUpload={async (files) => {
                      // Simulate upload process
                      const urls = files.map((file) =>
                        URL.createObjectURL(file),
                      );
                      setFormData((prev) => ({
                        ...prev,
                        mediaUrls: [...prev.mediaUrls, ...urls],
                      }));
                      return Promise.resolve();
                    }}
                  />

                  {formData.mediaUrls.length > 0 && (
                      <Textarea
                      label="Or enter existing Media URLs (one per line)"
                      value={formData.mediaUrls.join("\n")}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          mediaUrls: e.target.value
                            .split("\n")
                            .filter((url) => url.trim()),
                        }))
                      }
                      rows={3}
                      placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                      helperText="Enter one URL per line for multiple files"
                    />
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={formData.isPublic}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isPublic: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 border-gray-300 rounded"
                    style={{ color: 'var(--patreon-orange)' } as React.CSSProperties}
                    onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                    onBlur={(e) => { e.target.style.boxShadow = ''; }}
                  />
                  <label
                    htmlFor="isPublic"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Publish immediately
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isScheduled"
                    checked={formData.isScheduled}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isScheduled: e.target.checked,
                      }))
                    }
                    disabled={formData.isPublic}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label
                    htmlFor="isScheduled"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Schedule for later
                  </label>
                </div>

                {formData.isScheduled && (
                  <FormInput
                    label="Schedule Date & Time"
                    type="datetime-local"
                    value={formData.scheduledFor || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        scheduledFor: e.target.value,
                      }))
                    }
                    required
                    min={new Date().toISOString().slice(0, 16)}
                  />
                )}
              </div>
            </div>
            )}
          </ModalContent>

          <ModalFooter>
            <div className="flex items-center justify-between w-full">
              <Button
                variant="outline"
                onClick={() => setShowPreview(!showPreview)}
                type="button"
              >
                <EyeIcon className="w-5 h-5 mr-2" />
                {showPreview ? 'Edit' : 'Preview'}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  type="button"
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting} disabled={showPreview}>
                  {editingPost ? "Update Post" : "Create Post"}
                </Button>
              </div>
            </div>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default PostsPage;
